import { useDispatch, useSelector } from "react-redux";
import {
  toggleMobileNavbar,
  setActiveMenu,
  openSubMenu,
  closeSubMenu,
  closeMobileNavbar,
} from "../../redux/MobileSideBar";

import {
  arrow_left,
  arrow_right,
  menu_icon,
  toggle_down,
  toggle_up,
} from "../../helpers/constant/imageUrl";
import { SERVICES } from "../../page/services/constant";
import { SOLUTIONS } from "../../page/solutions/constant";
import { PRODUCTS } from "../../page/products/constant";
import { INDUSTRIES } from "../../page/industries/constant";
import { ABOUTUS } from "../../page/company/constant";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AtomButton from "../../atoms/button/button";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import "./mobilenavbar.scss";
import { useEffect, useRef } from "react";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";

const MENU_GROUPS = {
  services: SERVICES,
  solutions: PRODUCTS,
  product: SOLUTIONS,
  industries: INDUSTRIES,
  aboutus: ABOUTUS,
};

const MENU_LABELS = {
  services: "Services",
  product: "Products",
  solutions: "Solutions",
  industries: "Industries",
  aboutus: "About Us",
};

const MobileNavBar = () => {
  const sidebarRef = useRef(null);
  const subsidebarRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { isMobileNavbar, activeMenu, subMenuData, isSubMenuOpen } =
    useSelector((state) => state.mobileNavBar);

  const normalize = (path) => path?.replace(/\/+$/, "");

  //  helper: parent (with children) is active ONLY if one of its own children is active
  const isParentWithChildrenActive = (item, pathname) => {
    if (!item.segments?.length) return false;

    return item.segments.some(
      (seg) => normalize(pathname) === normalize(seg.path),
    );
  };

  const handleMenuClick = (key) => {
    if (activeMenu === key) {
      dispatch(setActiveMenu(null));
      dispatch(closeSubMenu());
    } else {
      dispatch(setActiveMenu(key));
      dispatch(closeSubMenu());
    }
  };

  const handleSubMenu = (item) => {
    if (!item.segments?.length) {
      navigate(item.path);
      dispatch(closeMobileNavbar());
      return;
    }
    dispatch(openSubMenu(item));
  };

  useEffect(() => {
    const current = normalize(location.pathname);

    let matchedMenu = null;
    let matchedItem = null;

    Object.entries(MENU_GROUPS).forEach(([key, list]) => {
      list.forEach((item) => {
        // exact parent match only
        if (item.path && current === normalize(item.path)) {
          matchedMenu = key;
          matchedItem = item;
        }

        // child match
        if (item.segments?.length) {
          item.segments.forEach((seg) => {
            if (current === normalize(seg.path)) {
              matchedMenu = key;
              matchedItem = item;
            }
          });
        }
      });
    });

    if (matchedMenu) {
      dispatch(setActiveMenu(matchedMenu));
      if (matchedItem?.segments?.length) {
        dispatch(openSubMenu(matchedItem));
      }
    }
  }, [location.pathname, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideMenu =
        sidebarRef.current && sidebarRef.current.contains(event.target);

      const clickedInsideSubMenu =
        subsidebarRef.current && subsidebarRef.current.contains(event.target);

      // ❗ Close only if clicked outside BOTH
      if (!clickedInsideMenu && !clickedInsideSubMenu) {
        dispatch(closeMobileNavbar());
      }
    };

    if (isMobileNavbar) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileNavbar, dispatch]);

  return (
    <>
      {/* MAIN SIDEBAR */}
      <div className={`menu_sidebar ${isMobileNavbar ? "active" : ""}`}>
        <div
          ref={sidebarRef}
          className={`menu ${isMobileNavbar ? "active" : ""}`}
        >
          <div
            className="menu_close"
            onClick={() => dispatch(toggleMobileNavbar())}
          >
            <CloseRoundedIcon style={{ width: 30, height: 30 }} />
          </div>

          <div className="menu_content">
            <ul>
              {Object.keys(MENU_GROUPS).map((key) => {
                const isOpen = activeMenu === key;
                const menuList = MENU_GROUPS[key];

                return (
                  <>
                    <li
                      onClick={() => handleMenuClick(key)}
                      className={activeMenu === key ? "active" : ""}
                    >
                      {MENU_LABELS[key]}
                      <img
                        loading="lazy"
                        className="toggle-icon"
                        src={isOpen ? toggle_up : toggle_down}
                        alt="arrow"
                      />
                    </li>

                    {isOpen && (
                      <div className="sublinks">
                        <ul>
                          {menuList.map((item) => (
                            <div className="sublinks_list" key={item.text}>
                              <li
                                onClick={() => handleSubMenu(item)}
                                className={`nav-links ${
                                  item.segments?.length
                                    ? isParentWithChildrenActive(
                                        item,
                                        location.pathname,
                                      )
                                      ? "active"
                                      : ""
                                    : normalize(location.pathname) ===
                                        normalize(item.path)
                                      ? "active"
                                      : ""
                                }`}
                              >
                                <img
                                  loading="lazy"
                                  src={item.img}
                                  className="sublink_icon"
                                  alt="arrow"
                                />
                                {item.text}
                              </li>
                              <img
                                loading="lazy"
                                src={arrow_right}
                                className="arrow_right"
                                alt="arrow"
                              />
                            </div>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                );
              })}

              <li>
                <NavLink
                  to="/careers"
                  onClick={() => dispatch(toggleMobileNavbar())}
                >
                  Careers
                </NavLink>
              </li>

              <li>
                <AtomButton
                  name="primaryButton"
                  color="primary"
                  variant="contained"
                >
                  Schedule a call
                </AtomButton>
              </li>
            </ul>
          </div>
        </div>

        {/* NESTED SUBMENU */}
        <div
          ref={subsidebarRef}
          className={`sub_menu ${isSubMenuOpen ? "active" : ""}`}
        >
          {subMenuData && (
            <>
              <div
                className="menu_close"
                onClick={() => dispatch(toggleMobileNavbar())}
              >
                <CloseRoundedIcon style={{ width: 30, height: 30 }} />
              </div>

              <div className="menu_content">
                <div className="back" onClick={() => dispatch(closeSubMenu())}>
                  <img
                    loading="lazy"
                    src={arrow_left}
                    className="back_arrow"
                    alt="arrow"
                  />
                  <p className="text_back">{subMenuData.text}</p>
                </div>

                <p className="segemnts_submenu">Segments</p>

                <ul>
                  {subMenuData.segments.map((seg) => (
                    <li
                      key={seg.text}
                      className={
                        normalize(location.pathname) === normalize(seg.path)
                          ? "active2"
                          : ""
                      }
                      onClick={() => {
                        navigate(seg.path);
                        dispatch(closeMobileNavbar());
                      }}
                    >
                      <NavLink to={seg.path} className="submenu_links">
                        {seg.text}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </div>

      {/* MENU ICON */}
      {!isMobileNavbar && (
        <div
          className="menu_bars"
          onClick={() => dispatch(toggleMobileNavbar())}
        >
          {/* <img  loading="lazy" src={menu_icon} className="hamburger" alt="arrow" /> */}
          <MenuRoundedIcon sx={{ color: "black", fontSize: "36px" }} />
        </div>
      )}
    </>
  );
};

export default MobileNavBar;
